/**
 * useHashRouter — Lightweight hash-based routing
 *
 * Sync app state với URL hash, hỗ trợ browser back/forward.
 * Không cần react-router, không cần server config.
 *
 * URLs:
 *   /#/translate                          → projects list (translating)
 *   /#/translate/{projectId}              → project detail
 *   /#/translate/{projectId}/{fileId}     → file detail
 *   /#/train                              → projects list (training)
 *   /#/train/{projectId}                  → project detail
 *   /#/train/{projectId}/{fileId}         → file detail
 *   /#/terminology                        → terminology
 *   /#/shop                               → credit shop (commercial)
 *   /#/privacy                            → privacy policy (commercial)
 *   /#/admin                              → admin dashboard
 *   /#/                                   → redirect to /translate
 */

import { useState, useEffect, useCallback } from 'react';
import { isCommercial } from '../config/appMode';

export interface RouteState {
  section: 'translate' | 'train' | 'terminology' | 'shop' | 'privacy' | 'admin';
  projectId?: string;
  fileId?: string;
}

export interface BreadcrumbItem {
  label: string;
  hash: string;
  isCurrent: boolean;
}

function parseHash(hash: string): RouteState {
  const path = hash.replace('#/', '').replace('#', '');
  const parts = path.split('/').filter(Boolean);

  if (parts.length === 0) return { section: 'translate' };

  const section = parts[0] as RouteState['section'];
  const validSections = ['translate', 'train', 'terminology', 'shop', 'privacy', 'admin'];

  if (!validSections.includes(section)) return { section: 'translate' };

  return {
    section,
    projectId: parts[1] || undefined,
    fileId: parts[2] || undefined,
  };
}

function buildHash(route: RouteState): string {
  let hash = `#/${route.section}`;
  if (route.projectId) hash += `/${route.projectId}`;
  if (route.fileId) hash += `/${route.fileId}`;
  return hash;
}

export function useHashRouter() {
  const [route, setRoute] = useState<RouteState>(() => {
    return parseHash(window.location.hash || `#/${isCommercial ? 'translate' : 'translate'}`);
  });

  // Listen to hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      setRoute(parseHash(window.location.hash));
    };
    window.addEventListener('hashchange', handleHashChange);

    // Set initial hash if empty
    if (!window.location.hash) {
      window.location.hash = '#/translate';
    }

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Navigate to new route
  const navigate = useCallback((newRoute: Partial<RouteState>) => {
    const merged: RouteState = {
      section: newRoute.section || route.section,
      projectId: newRoute.section && newRoute.section !== route.section ? newRoute.projectId : (newRoute.projectId ?? route.projectId),
      fileId: newRoute.fileId,
    };

    // Clear projectId/fileId if navigating to new section without them
    if (newRoute.section && !newRoute.projectId) {
      merged.projectId = undefined;
      merged.fileId = undefined;
    }

    const hash = buildHash(merged);
    window.location.hash = hash;
    setRoute(merged);
  }, [route]);

  // Convenience navigators
  const navigateToSection = useCallback((section: RouteState['section']) => {
    navigate({ section });
  }, [navigate]);

  const navigateToProject = useCallback((projectId: string) => {
    navigate({ projectId, fileId: undefined });
  }, [navigate]);

  const navigateToFile = useCallback((projectId: string, fileId: string) => {
    navigate({ projectId, fileId });
  }, [navigate]);

  const goBack = useCallback(() => {
    if (route.fileId) {
      // file → project
      navigate({ projectId: route.projectId, fileId: undefined });
    } else if (route.projectId) {
      // project → section list
      navigate({ section: route.section, projectId: undefined });
    }
    // else: already at top level
  }, [route, navigate]);

  // Build breadcrumb items
  const buildBreadcrumbs = useCallback((
    projectName?: string,
    fileName?: string
  ): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];

    // Section label
    const sectionLabels: Record<string, string> = {
      translate: isCommercial ? 'Dịch tài liệu' : 'Translating',
      train: 'Training',
      terminology: 'Terminology',
      shop: 'Mua Credits',
      privacy: 'Chính sách bảo mật',
      admin: 'Admin',
    };

    const sectionLabel = sectionLabels[route.section] || route.section;
    const isAtSection = !route.projectId && !route.fileId;

    items.push({
      label: sectionLabel,
      hash: `#/${route.section}`,
      isCurrent: isAtSection,
    });

    // Project level
    if (route.projectId) {
      const isAtProject = !route.fileId;
      items.push({
        label: projectName || route.projectId.slice(0, 8) + '...',
        hash: `#/${route.section}/${route.projectId}`,
        isCurrent: isAtProject,
      });
    }

    // File level
    if (route.fileId) {
      items.push({
        label: fileName || route.fileId.slice(0, 8) + '...',
        hash: `#/${route.section}/${route.projectId}/${route.fileId}`,
        isCurrent: true,
      });
    }

    return items;
  }, [route]);

  return {
    route,
    navigate,
    navigateToSection,
    navigateToProject,
    navigateToFile,
    goBack,
    buildBreadcrumbs,
  };
}
